import type { Faq } from '../components/blocks/FaqSection'
import type { ProcessStep } from '../components/blocks/ProcessSteps'
import type { ChecklistContent } from '../components/blocks/CheckList'
import type { ProseSectionContent } from '../components/blocks/ProseSection'

/**
 * SERVICE CONTENT — SINGLE SOURCE OF TRUTH
 *
 * WORDPRESS MAPPING: one entry === one `service` Custom Post Type record.
 * Field names below map 1:1 onto the ACF field group `service_fields`.
 *
 * Previously this data was split between this file, hard-coded arrays inside
 * each service page component, and a `homepageServiceCopy` override object in
 * Home.tsx. It is now unified so the WordPress migration is a straight import.
 */

export interface Service {
  /** ACF: post_name (slug) */
  id: string
  /** ACF: service_title — full formal title */
  title: string
  /** ACF: service_short_title — used in cards and cross-links */
  shortTitle: string
  /** ACF: service_eyebrow */
  eyebrow: string
  /** ACF: service_tagline — the italic promise line */
  tagline: string
  /** ACF: service_summary — long-form description */
  summary: string
  /** ACF: service_card_summary — condensed copy for grids */
  cardSummary: string
  /** ACF: service_cta_label */
  exploreLabel: string
  /** ACF: service_price_display */
  price: string
  /** ACF: service_price_numeric — schema.org Offer.price */
  priceNumeric: string
  /** ACF: service_price_detail */
  priceDetail: string
  /** ACF: service_duration */
  duration: string
  /** ACF: service_format */
  format: string
  /** ACF: service_image */
  image: string
  /** ACF: service_image_alt */
  imageAlt: string
  /** ACF: post permalink */
  link: string
  /** ACF: service_featured_testimonial (relationship → testimonial CPT) */
  featuredTestimonialId: string
  /** ACF: service_checklist (repeater) — "Is this path for you?" */
  checklist: ChecklistContent
  /** ACF: service_prose_sections (flexible content → block_prose) */
  sections: readonly ProseSectionContent[]
  /** ACF: service_pull_quote (group, optional) */
  pullQuote?: { eyebrow?: string; quote: string; author: string }
  /** ACF: service_secondary_checklist (repeater, optional) */
  secondaryChecklist?: ChecklistContent
  /** ACF: service_steps (repeater) */
  steps: {
    eyebrow: string
    heading: string
    intro?: string
    items: readonly ProcessStep[]
  }
  /** ACF: service_faqs (repeater) — powers FAQPage schema for GEO */
  faqs: readonly Faq[]
  /** ACF: seo_title / seo_description */
  seo: { title: string; description: string }
}

export const servicesData: readonly Service[] = [
  {
    id: 'meditation-between-worlds',
    title: 'Meditation Between the Worlds',
    shortTitle: 'Meditation Between Worlds',
    eyebrow: 'Guided practice',
    tagline: 'For those ready to reconnect from within.',
    summary:
      'Not passive sitting, but an active guided journey into awareness, presence, and personal transformation. Learn to move beyond old patterns and create space for new possibilities in live or online sessions.',
    cardSummary:
      'An active guided journey inward — a practice of presence, awareness and inner transformation.',
    exploreLabel: 'Explore Meditation',
    price: 'From €15 / session',
    priceNumeric: '15',
    priceDetail: 'One-time hourly fee €15 • Monthly fee €50 (paid in advance)',
    duration: 'Approx. 60 minutes',
    format: 'In-person or Online',
    image:
      'https://cdn.magicpatterns.com/uploads/tCDtXzKaLnkXemwhUXS3MT/milangela_start_your_journey_001.webp',
    imageAlt:
      'Milangela holding a pendulum during a guided meditation session',
    link: '/start-your-journey/meditation-between-worlds',
    featuredTestimonialId: 'metka',
    checklist: {
      eyebrow: 'A path inward',
      heading: 'Is this path for you?',
      lead: 'Meditation Between Worlds may be the right path if you are looking to:',
      items: [
        're-connect with yourself on a deeper level',
        'create space beyond old patterns of thinking and behaviour',
        'strengthen your awareness and presence',
        "better understand your body's responses and inner experience",
        'develop a regular practice that supports lasting personal transformation',
      ],
    },
    sections: [
      {
        id: 'what-is-meditation-between-worlds',
        eyebrow: 'Presence in practice',
        heading: 'What is Meditation Between Worlds?',
        variant: 'bordered',
        paragraphs: [
          {
            text: 'This meditation is not a moment of escape, but a conscious practice of presence.',
          },
          {
            text: 'Each session begins with a short introduction — a reminder of why you are here, what may arise during the meditation and how to meet your body’s responses with awareness.',
          },
          {
            text: 'With your eyes closed and attention turned inward, you are guided through an exploration of your body, its reactions, discomfort, beliefs and limitations. Rather than something to avoid, these become valuable starting points for change.',
          },
          {
            text: 'Over time, this practice helps you reconnect with your true self, develop new inner pathways and meet both physical and mental resistance with greater awareness and strength.',
          },
          { text: 'Stillness is not the goal.', style: 'lead' },
          {
            text: 'It is the beginning of a journey towards conscious responsibility and active transformation.',
            style: 'accent',
          },
        ],
      },
    ],
    secondaryChecklist: {
      eyebrow: 'A lasting practice',
      heading: 'Regular practice',
      intro:
        'The meditation is designed as an ongoing practice rather than a one-time experience.',
      lead: 'Through regular meditation, you gradually learn to:',
      variant: 'panel',
      items: [
        'recognise old patterns of thought and behaviour',
        'reconnect with your true self',
        'approach resistance with greater awareness',
        'develop inner clarity and groundedness',
        'become more present in everyday life',
      ],
    },
    steps: {
      eyebrow: 'The experience',
      heading: 'What to expect',
      intro: 'Each session follows a gentle and structured flow.',
      items: [
        {
          title: 'Introduction',
          description:
            'A short opening to help you settle, understand the intention of the practice and prepare for the experience.',
        },
        {
          title: 'Guided Meditation',
          description:
            'A carefully guided inward journey through awareness of the body, thoughts and emotions.',
        },
        {
          title: 'Reflection',
          description:
            'A gentle return from the meditation, allowing space for observation and integration of the experience.',
        },
        {
          title: 'Continuing the practice',
          description:
            'With regular participation, the practice gradually becomes part of everyday life, supporting greater presence, clarity and connection with yourself.',
        },
      ],
    },
    faqs: [
      {
        question: 'Do I need any meditation experience to join?',
        answer:
          'No. Every session is fully guided from beginning to end, so complete beginners are welcome. You will be talked through what to expect before the practice starts, and there is nothing you need to prepare or know in advance.',
      },
      {
        question: 'Is Meditation Between Worlds available online?',
        answer:
          'Yes. Sessions run both in person and online, and the online experience follows exactly the same structure. All you need is a quiet space, headphones, and somewhere comfortable to sit or lie down.',
      },
      {
        question: 'How much does a session cost?',
        answer:
          'A single session is €15. A monthly pass, paid in advance, is €50 and covers all sessions within that month.',
      },
      {
        question: 'How often should I attend?',
        answer:
          'The practice is designed as an ongoing one rather than a one-off experience. Most people notice the deepest shifts when they attend regularly, which is why the monthly option exists.',
      },
    ],
    seo: {
      title: 'Meditation Between Worlds',
      description:
        'An active guided meditation practice for presence, awareness and inner transformation. Live or online sessions from €15 with Branka Androjna.',
    },
  },
  {
    id: 'sound-infused-renewal',
    title: 'Sound – Infused Renewal Transformation',
    shortTitle: 'Sound-Infused Renewal',
    eyebrow: 'Biofield Tuning',
    tagline: 'When your body is asking for balance.',
    summary:
      "Through Biofield Tuning and the gentle vibration of tuning forks, this one-to-one session supports your body's natural ability to release tension, restore harmony, and reconnect with its own intelligence.",
    cardSummary:
      'A one-to-one Biofield Tuning session using sound and vibration to support balance, release and deep relaxation.',
    exploreLabel: 'Explore Sound Therapy',
    price: '€100 / session',
    priceNumeric: '100',
    priceDetail:
      'Includes an initial consultation, the therapy session, and a final check-in with feedback.',
    duration: 'Full session with consultation',
    format: 'In-person (Studio)',
    image:
      'https://cdn.magicpatterns.com/uploads/4hVZppYvuPFeSPcZrmEdmK/milangela_start_your_journey_002.webp',
    imageAlt: 'Milangela using tuning forks in a Biofield Tuning session',
    link: '/start-your-journey/sound-infused-renewal',
    featuredTestimonialId: 'dr-lucille-necas',
    checklist: {
      eyebrow: 'A gentle way to reset',
      heading: 'Is this path for you?',
      lead: 'This session may be right for you if you...',
      items: [
        'feel emotionally or physically overwhelmed',
        'experience ongoing stress or inner tension',
        'find it difficult to slow down and fully relax',
        'wish to reconnect with your body and restore a sense of balance',
        'are looking for a gentle, holistic approach to wellbeing',
      ],
    },
    sections: [
      {
        id: 'what-is-sound-renewal',
        eyebrow: 'Sound and natural rhythm',
        heading: 'What is Sound-Infused Renewal?',
        variant: 'bordered',
        paragraphs: [
          {
            text: 'Sound-Infused Renewal is a one-to-one Biofield Tuning session that uses the gentle vibrations of tuning forks to support your body’s natural ability to restore balance.',
          },
          {
            text: 'Stress, emotional experiences and everyday life can create tension and disruption in our natural rhythm. Through carefully applied sound and vibration, the body is offered an opportunity to recognise these areas of imbalance and gradually return to greater harmony.',
          },
          {
            text: 'Rather than changing the body, the intention is to create the conditions in which it can reconnect with its own natural intelligence and balance. This aligns with the principles of Biofield Tuning, a sound-based method developed by Eileen Day McKusick using tuning forks to support relaxation and regulation of the nervous system.',
          },
        ],
      },
      {
        id: 'how-session-works',
        eyebrow: 'Gentle attunement',
        heading: 'How does the session work?',
        variant: 'panel',
        paragraphs: [
          {
            text: 'During the session, tuning forks are used around the body’s biofield.',
          },
          {
            text: 'Changes in tone, vibration and resistance help identify areas where stress or energetic congestion may be present. Through gentle, patient attunement, these areas are gradually supported towards greater coherence and flow.',
          },
          {
            text: 'As the body is largely composed of water, sound travels deeply through its tissues. The coherent frequencies of the tuning forks offer the body a stable reference point, allowing it to respond in its own way and at its own pace. This description reflects the principles taught within the Biofield Tuning method.',
          },
        ],
      },
    ],
    pullQuote: {
      eyebrow: 'A guiding perspective',
      quote: 'The Field is the Sole Governing Agency of the Particle.',
      author: 'Albert Einstein',
    },
    secondaryChecklist: {
      eyebrow: 'Held with care',
      heading: 'Your session includes',
      items: [
        'Initial consultation',
        'Full Biofield Tuning session',
        'Final reflection and feedback',
      ],
    },
    steps: {
      eyebrow: 'The experience',
      heading: 'What to expect',
      items: [
        {
          title: 'Initial conversation',
          description:
            'We begin with a short conversation about how you are feeling and what has brought you to the session.',
        },
        {
          title: 'Biofield Tuning session',
          description:
            "You relax while the tuning forks are used to gently support your body's natural process of rebalancing.",
        },
        {
          title: 'Integration',
          description:
            'We finish with a short check-in, giving you space to reflect on your experience and ask any questions.',
        },
      ],
    },
    faqs: [
      {
        question: 'What is Biofield Tuning?',
        answer:
          'Biofield Tuning is a sound-based method developed by Eileen Day McKusick that uses tuning forks around the body to support relaxation and regulation of the nervous system. Changes in tone and resistance help identify areas where stress may be held, and gentle attunement supports the body back towards balance.',
      },
      {
        question: 'What does a Biofield Tuning session feel like?',
        answer:
          'Most people describe a deep state of relaxation, similar to the edge of sleep. You stay fully clothed and simply rest while the tuning forks are used around you. Some people feel warmth, tingling or emotional release; others simply feel calm.',
      },
      {
        question: 'Is Biofield Tuning a medical treatment?',
        answer:
          'No. This is a complementary wellbeing practice, not a medical or psychological treatment. It does not diagnose, treat or cure any condition and is not a substitute for medical care from a qualified professional.',
      },
      {
        question: 'How long is a session and what does it include?',
        answer:
          'A full session is €100 and includes an initial consultation, the Biofield Tuning session itself, and a final check-in with feedback so you have space to reflect and ask questions.',
      },
    ],
    seo: {
      title: 'Sound-Infused Renewal — Biofield Tuning',
      description:
        'A one-to-one Biofield Tuning session using tuning forks to release tension and restore balance. In-person sessions with a certified practitioner.',
    },
  },
  {
    id: 'sopotje',
    title: 'Sopotje – Guidance and Presence Support Sessions',
    shortTitle: 'Guidance & Presence — Sopotje',
    eyebrow: 'Guidance & Presence',
    tagline: 'When what you need most is someone to walk beside you.',
    summary:
      'A confidential space for reflection, emotional clarity, and compassionate conversation. No judgement. No pressure. Just presence.',
    cardSummary:
      'A grounded and compassionate space for reflection, emotional clarity and honest conversation.',
    exploreLabel: 'Explore Guidance',
    price: '€60 / hour',
    priceNumeric: '60',
    priceDetail: 'Hourly fee €60',
    duration: 'Approx. 60 minutes',
    format: 'In-person or Online',
    image:
      'https://cdn.magicpatterns.com/uploads/2ENtRhvsfRmgW8Hw9YXEF8/milangela_start_your_journey_003.webp',
    imageAlt:
      'Milangela taking notes during a guidance and presence session',
    link: '/start-your-journey/sopotje',
    featuredTestimonialId: 'mari-bartoo-jacobson',
    checklist: {
      eyebrow: 'A space to be heard',
      heading: 'Is this path for you?',
      lead: 'This space may be right for you if you...',
      items: [
        'are going through a period of change or uncertainty',
        'feel emotionally overwhelmed or mentally exhausted',
        'need someone who will truly listen without judgement',
        'are searching for greater clarity before taking your next step',
        'simply long for a safe space where you can pause and hear yourself again',
      ],
    },
    sections: [
      {
        id: 'what-is-sopotje',
        eyebrow: 'Walking alongside',
        heading: 'What is Sopotje?',
        variant: 'bordered',
        paragraphs: [
          { text: 'Sopotje literally means walking alongside someone.' },
          { text: 'It reflects the essence of my work.' },
          { text: 'I don’t walk ahead of you, showing you where to go.' },
          { text: 'I don’t walk behind you, pushing you forward.' },
          { text: 'I walk beside you.', style: 'lead' },
          {
            text: 'Together we create a space where you can slow down, breathe and reconnect with yourself.',
          },
          {
            text: 'A space where you are not analysed or judged, but accepted exactly as you are.',
          },
          { text: 'Sometimes the greatest support is not advice.' },
          {
            text: 'Sometimes it is simply being deeply heard.',
            style: 'accent',
          },
        ],
      },
      {
        id: 'my-role',
        eyebrow: 'Clarity, not instruction',
        heading: 'My role',
        variant: 'panel',
        paragraphs: [
          { text: 'I am not a psychologist.', style: 'statement' },
          { text: 'I am not a doctor.', style: 'statement' },
          { text: 'I don’t offer diagnoses or ready-made solutions.' },
          { text: 'I offer presence.', style: 'lead' },
          {
            text: 'Warm, grounded and compassionate presence.',
            style: 'accent',
          },
          {
            text: 'My role is not to tell you what to do, but to accompany you as you search for your own clarity, strength and inner compass.',
          },
        ],
      },
      {
        id: 'my-approach',
        eyebrow: 'A lived understanding',
        heading: 'My approach',
        paragraphs: [
          {
            text: 'My greatest teacher has been life itself.',
            style: 'lead',
          },
          {
            text: 'Especially the experience of illness, which taught me that while we cannot always choose what happens to us, we can choose how present we are within it.',
          },
          { text: 'Since then, I no longer carry only my own story.' },
          { text: 'I carry a deep respect for the journeys of others.' },
          {
            text: 'That is why I can walk beside you with compassion, honesty and without judgement.',
          },
        ],
      },
    ],
    steps: {
      eyebrow: 'The experience',
      heading: 'What to expect',
      intro:
        'Each conversation is different because every person arrives with their own story. What remains the same is the space we create together.',
      items: [
        {
          title: 'A welcoming conversation',
          description: 'We begin wherever you are today.',
        },
        {
          title: 'Reflection',
          description:
            'Together we explore your thoughts, emotions and questions without rushing towards solutions.',
        },
        {
          title: 'Finding your own direction',
          description:
            'Rather than giving advice, I support you in reconnecting with your own inner clarity and wisdom.',
        },
      ],
    },
    faqs: [
      {
        question: 'What does "Sopotje" mean?',
        answer:
          'Sopotje literally means walking alongside someone. It reflects the essence of the work: not walking ahead of you showing the way, not walking behind you pushing you forward, but walking beside you.',
      },
      {
        question: 'Is this therapy or counselling?',
        answer:
          'No. Branka is not a psychologist or a doctor and does not offer diagnoses, treatment plans or ready-made solutions. Sopotje offers compassionate presence and a confidential space for reflection. If you need clinical support, please speak to a qualified mental health professional.',
      },
      {
        question: 'Are sessions confidential?',
        answer:
          'Yes. Everything shared in a session stays between you and Branka. The space is deliberately free of judgement, analysis and pressure.',
      },
      {
        question: 'How much does a session cost?',
        answer:
          'Sessions are €60 per hour and can take place either in person or online, whichever feels more comfortable for you.',
      },
    ],
    seo: {
      title: 'Guidance & Presence — Sopotje',
      description:
        'A confidential space for reflection, emotional clarity and compassionate conversation. In-person or online support sessions at €60 per hour.',
    },
  },
]

export function getService(id: string): Service {
  const service = servicesData.find((item) => item.id === id)
  if (!service) throw new Error(`Unknown service: ${id}`)
  return service
}

export function getOtherServices(id: string): readonly Service[] {
  return servicesData.filter((item) => item.id !== id)
}
